import clsx from "clsx";
import { TFunction } from "i18next";
import { FC, ReactElement, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { TemplateWrapper } from "@ryde/controls";
import { useBulkEditFormContext, useCrudEditContext } from "@ryde/redux";
import {
  fieldNameMetadataResolve,
  getIsDisabledByCondition,
  getIsDisabledByConditionBulk,
  getIsShownByConditionRenderTemplate,
  ICustomField,
  IExtendableDto,
  isEntityWithAdminMode,
  isInnerItemEditingAtom,
  Log,
  profileDtoAtom,
  profilePermissionsSelector,
  systemSettingsDtoAtom,
  useHasPermissionCustomField,
} from "@ryde/shared";

import { useRideHeaderPriceVisibility } from "../../drawers/components/ride-edit-form/components/hooks/use-ride-header-price-visibility";
import { getBulkDynamicTemplateElement } from "./get-bulk-dynamic-template-element";
import { getDynamicTemplateElement } from "./get-dynamic-template-element";

export type TFormVariant = "bulk" | "single";

interface IRenderTemplateProps extends ICustomField {
  formVariant?: TFormVariant;
}

type TResolveTemplateGetter = (
  customField: ICustomField,
  translateFunction: TFunction,
) => ReactElement;

const resolveTemplateGetter = (
  formVariant: TFormVariant,
): TResolveTemplateGetter => {
  if (formVariant === "bulk") {
    return getBulkDynamicTemplateElement;
  }

  return getDynamicTemplateElement;
};

export const RenderTemplate: FC<IRenderTemplateProps> = props => {
  const {
    id,
    name: fieldNameRaw,
    isColumn,
    formFieldTypeId,
    formVisibleCondition,
    readPermission,
    updatePermission,
    formAttributes,
    formVariant = "single",
  } = props;
  const systemSettingsDto = useRecoilValue(systemSettingsDtoAtom);
  const { t } = useTranslation();
  const { getFieldPermissions } = useHasPermissionCustomField();
  const { getValues, setValue, unregister, trigger } = useFormContext();
  const {
    entity: crudEntity,
    data: { isDuplicate, isEdit, dto },
  } = useCrudEditContext();
  const {
    entity: bulkFormEntity,
    isBulkEditForm,
    selectedRowsValues,
  } = useBulkEditFormContext();
  const entityComputed = crudEntity || bulkFormEntity;
  const entityNormalized =
    entityComputed === "unknown" ? undefined : entityComputed;

  const { isFieldVisible, isFieldDisabled } = useMemo(
    () =>
      getFieldPermissions({
        readPermission,
        updatePermission,
        formEntity: entityNormalized,
      }),
    [entityNormalized, getFieldPermissions, readPermission, updatePermission],
  );

  const profile = useRecoilValue(profileDtoAtom);
  const roleDataItem = useRecoilValue(profilePermissionsSelector);
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  const fieldNameResolved = fieldNameMetadataResolve(fieldNameRaw, isColumn);
  const isAdminModeEntity = useMemo(() => {
    return !!entityNormalized && isEntityWithAdminMode(entityNormalized);
  }, [entityNormalized]);

  const isManualPriceField = fieldNameResolved === "metadata.manualPrice";
  const isSupplierManualPriceField =
    fieldNameResolved === "metadata.supplierManualPrice";
  const isSupplierPriceField = fieldNameResolved === "supplierPrice";

  const { shouldShowClientPrice, shouldShowSupplierPrice } =
    useRideHeaderPriceVisibility();

  const shouldHideByAdminMode = useMemo(() => {
    if (!isAdminModeEntity) {
      return false;
    }

    if (isManualPriceField && !shouldShowClientPrice) {
      return true;
    }

    if (
      (isSupplierManualPriceField || isSupplierPriceField) &&
      !shouldShowSupplierPrice
    ) {
      return true;
    }

    return false;
  }, [
    isAdminModeEntity,
    isManualPriceField,
    isSupplierManualPriceField,
    isSupplierPriceField,
    shouldShowClientPrice,
    shouldShowSupplierPrice,
  ]);

  const propsNormalized: ICustomField = {
    ...props,
    ...{ name: fieldNameResolved },
    dateFormat: profile?.metadata?.dateFormat,
    timeFormat: profile?.metadata?.timeFormat,
  };

  const curValues = getValues();

  const isShownByCondition = getIsShownByConditionRenderTemplate({
    formValues: curValues,
    formVisibleCondition,
    isCheckDtoValuesForVisibleConditions:
      formAttributes?.checkDtoValuesForVisibleConditions ?? false,
    dto: dto as IExtendableDto,
    isCheckSystemParamValuesForVisibleConditions:
      formAttributes?.checkSystemParamValuesForVisibleConditions ?? false,
    systemParams: systemSettingsDto?.metadata ?? {},
    permissions: roleDataItem?.permissions,
  });

  // TODO refactor this resolver
  // The bulk form is seeded from one arbitrary selected row, so a per-record condition (e.g. a booking's
  // status) must be judged against the whole selection instead — otherwise that one row locks the field
  // for every other record.
  const isDisabledByCondition = isBulkEditForm
    ? getIsDisabledByConditionBulk({
        selectedRowsValues,
        conditions: formAttributes?.disableCondition,
        permissions: roleDataItem?.permissions,
      })
    : getIsDisabledByCondition({
        fieldValues: curValues,
        conditions: formAttributes?.disableCondition,
        permissions: roleDataItem?.permissions,
      });

  propsNormalized.formAttributes = {
    ...formAttributes,
    disabled: isFieldDisabled
      ? true
      : (formAttributes?.disabled ?? isDisabledByCondition),
  };

  useEffect(() => {
    if (
      fieldNameResolved &&
      isShownByCondition &&
      isFieldVisible &&
      formAttributes?.newItemValue
    ) {
      const controlValue = getValues(fieldNameResolved);
      const recordId = getValues("id");

      if (!recordId && !isDuplicate && controlValue === undefined) {
        Log.debug(
          `Set default value to  template field ${fieldNameResolved}, value:`,
          formAttributes?.newItemValue,
        );
        setValue(fieldNameResolved, formAttributes?.newItemValue, {
          shouldDirty: true,
        });
      }
    }
  }, [
    formAttributes?.newItemValue,
    getValues,
    isDuplicate,
    isShownByCondition,
    fieldNameResolved,
    setValue,
    isFieldVisible,
  ]);

  // Subscribed rather than read from getValues(): this template no longer
  // re-renders on every keystroke in the form, so the dependant-validation
  // effect below needs its own subscription to this control value.
  const formValue = useWatch({ name: fieldNameResolved, exact: true });

  useEffect(() => {
    const dependantValues = formAttributes?.validationDependsOn;

    if (!dependantValues?.length) {
      return;
    }

    for (const dependantValue of dependantValues) {
      Log.debug(
        "Trigger dependant controls validation for ",
        dependantValue,
        formValue,
      );
      trigger(dependantValue);
    }
  }, [formValue, formAttributes?.validationDependsOn, trigger]);

  useEffect(() => {
    if (!isShownByCondition && curValues[fieldNameResolved] !== undefined) {
      Log.debug(
        `Unregister hidden by condition form control "${fieldNameResolved}"`,
      );
      unregister(fieldNameResolved);
    }
  }, [isShownByCondition, fieldNameResolved, curValues, unregister]);

  if (!isShownByCondition) {
    Log.debug(
      `Field "${fieldNameResolved}" HIDDEN by condition, visible only if:`,
      formVisibleCondition,
      ", form values snapshot:",
      { ...curValues },
    );

    return null;
  }

  if (!isFieldVisible) {
    Log.debug(
      `Field "${fieldNameResolved}" HIDDEN by readPermissionId, visible only for:`,
      readPermission,
    );
    return null;
  }

  if (shouldHideByAdminMode) {
    Log.debug(
      `Field "${fieldNameResolved}" HIDDEN by admin mode visibility check`,
    );
    return null;
  }

  if ((isEdit || formVariant === "bulk") && formAttributes?.isAddOnly) {
    Log.debug(
      `Field "${fieldNameResolved}" HIDDEN because isAddOnly: ${formAttributes.isAddOnly}`,
    );
    return null;
  }

  const { leftOffsetLevel, customContainerTopMargin } = formAttributes || {};
  const resolvedGetter = resolveTemplateGetter(formVariant);

  return (
    <TemplateWrapper
      data-e2e-field-id={id}
      data-e2e-field-name={fieldNameResolved}
      data-e2e-field-type={formFieldTypeId}
      data-e2e-is-column={isColumn}
      $leftOffsetLevel={leftOffsetLevel}
      className={clsx(
        "template_wrapper",
        isInnerItemEditing && "template-wrapper-blur",
      )}
      $customContainerTopMargin={customContainerTopMargin}
    >
      {resolvedGetter(propsNormalized, t)}
    </TemplateWrapper>
  );
};
